/* eslint-disable @typescript-eslint/no-empty-function */
import * as React from 'react';
import { Formik, FormikValues, FormikProps, FormikErrors } from 'formik';
import { useIntl } from 'react-intl';
import {
  CmdCalendar,
  CmdModal,
  CmdModalFooterButton,
  CmdRadioButton,
} from '@commander-services/gui-components';
import { useRecoilState, useRecoilValue } from 'recoil';
import dayjs from 'dayjs';
import FormikValidatorService, { IInput } from '../../../Services/FormikValidatorService';
import FormikService from '../../../Services/FormikService';
import CmdField from '../../Forms/CmdField';
import ChipSelect from '../../Forms/Fields/ChipSelect';
import * as ExportState from '../../Export/ExportState';
import { IVehicles } from '../../Vehicles/types';
import { IMessageReportCreated } from '../../SocketConnection/types';
import { vehiclesAtom } from '../../../store/recoil/vehicles';
import { getRequest, postRequest } from '../../../Services/HttpService';
import { AxiosResponse } from 'axios';

interface IFormProps {
  closeCallback: () => void;
  fileName: string;
  actionUrl: string;
  formats: string[];
}

export default function RidebookForm(props: IFormProps) {
  const { formatMessage: f } = useIntl();

  const [loader, setLoader] = React.useState<boolean>(false);
  const [isActiveExport, setIsActiveExport] = useRecoilState<false | string>(ExportState.isActive);
  // const [percentage, setPercentage] = useRecoilState<number>(ExportState.percentage);
  const [exportData, setExportData] = useRecoilState<IMessageReportCreated | null>(
    ExportState.data
  );
  // const [exportRequests, setExportRequests] = useRecoilState<string[]>(ExportState.exportRequests);

  const vehicles = useRecoilValue<IVehicles>(vehiclesAtom);
  const vehiclesOptions = vehicles.map((item: any) => {
    return {
      ...item,
      value: item.id,
      item: `${item.licenceNumber} - ${item.name}`,
    };
  });

  const parseDateToday0 = new Date(new Date().setHours(0, 0, 0, 0));
  const parseDateToday24 = new Date(new Date().setHours(23, 59, 59, 999));

  const [data, setData] = React.useState<IInput[]>([
    {
      indexId: 'dateFrom',
      title: f({ id: 'reports.dateFrom' }),
      readonly: false,
      disabled: false,
      type: 'datepicker',
      value: parseDateToday0,
      placeholder: '',
      rules: [],
    },

    {
      indexId: 'dateTo',
      title: f({ id: 'reports.dateTo' }),
      readonly: false,
      disabled: false,
      type: 'datepicker',
      value: parseDateToday24,
      placeholder: '',
      rules: [],
    },

    {
      indexId: 'vehicleIds',
      title: f({ id: 'reports.vehicleIds' }),
      readonly: false,
      disabled: false,
      type: 'multipleSelect',
      value: [],
      placeholder: f({ id: 'suppliersForm.choose' }),
      rules: [],
      options: vehiclesOptions,
    },

    {
      indexId: 'format',
      title: f({ id: 'reports.format' }),
      type: 'simpleSelect',
      readonly: false,
      disabled: false,
      value: '',
      placeholder: '',
      rules: [],
      options: props.formats
        ? props.formats.map((value: string) => {
            return { value, item: value };
          })
        : [],
    },
  ]);

  const formData: IInput[] = Object.assign(data);

  const getValidation = async () => {
    setLoader(true);

    const response: AxiosResponse | false = await getRequest(`/v2/report/validation?reportId=5`, { returnAxiosResponse: true });

    if (response && response.data && response.status === 200 && response.data.report) {
      const fields = response.data.report.form;

      Object.values(formData).map((item: any) => {
        Object.keys(fields).map((field: any) => {
          if (item.indexId === 'vehicleId') {
            item.rules = [{ param: 'required', value: true, message: f({ id: 'form.required' }) }];
          } else if (item.indexId === 'format' && response.data.report.userSettings) {
            item.value = response.data.report.userSettings.format || 'xlsx';
            item.rules = fields.format;
          } else if (item.indexId === field) {
            item.rules = fields[item.indexId];
          }
        });
      });

      setData({
        ...data,
        ...formData,
      });
    }
    setLoader(false);
  };

  React.useEffect(() => {
    getValidation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOnSubmit = async (formValues: FormikValues) => {
    setLoader(true);

    const dateFrom = dayjs(formValues.dateFrom).format('DD.MM.YYYY');
    const dateTo = dayjs(formValues.dateTo).format('DD.MM.YYYY');
    let fileTitle = props.fileName.replace('{dateFrom}', dateFrom);
    fileTitle = fileTitle.replace('{dateTo}', dateTo);
    const fileName = `${fileTitle}.${formValues.format}`;
    if (exportData) {
      setExportData(null);
      // setPercentage(0);
      // setExportRequests([]);
    }

    const res: AxiosResponse | false = await postRequest(props.actionUrl.replace('/api', ''), formValues, { returnAxiosResponse: true });
    if (res && res.data && res.data.status === 'ok') {
      setIsActiveExport(res.data.jobId);
      // const requests: string[] = JSON.parse(JSON.stringify(exportRequests));
      // if (!requests.includes(res.data.jobId)) {
      //   requests.push(res.data.jobId);
      // }
      // setExportRequests(requests);
    }

    setLoader(false);
    props.closeCallback();
  };

  const getRangeValidity = (fromDate: Date, toDate: Date) => {
    const refDate = new Date(toDate.getFullYear(), toDate.getMonth() - 3, toDate.getDate());
    if (refDate.getTime() > fromDate.getTime()) {
      return true;
    }
    return false;
  };

  const handleValidate = (values: FormikValues): FormikErrors<FormikValues> => {
    const errors = FormikValidatorService.validate(values, Object.values(data));
    const fromDate = dayjs(values.dateFrom);
    const toDate = dayjs(values.dateTo);
    if (fromDate.isAfter(toDate)) {
      errors.dateFrom = f({ id: 'datepicker.spanFromToValidation' });
    }
    if (getRangeValidity(values.dateFrom, values.dateTo)) {
      errors.dateFrom = f({
        id: 'errors.reports.cannotSelectDateLongerThan3Month',
      });
    }
    console.info('Validation errors: ', errors);
    return errors;
  };

  const initialValues = FormikService.setInitialValues(Object.values(data));

  return (
    <Formik
      enableReinitialize={true}
      initialValues={initialValues}
      validate={handleValidate}
      onSubmit={handleOnSubmit}
    >
      {(formikProps: FormikProps<FormikValues>) => {
        const prepareSubmit = async () => {
          const errors = await formikProps.validateForm();
          if (Object.keys(errors).length === 0) {
            handleOnSubmit(formikProps.values);
          } else {
            const fieldsToBeTouched = Object.create(errors);
            formikProps.setTouched(FormikService.toBoolean(fieldsToBeTouched));
          }
        };
        const buttons: CmdModalFooterButton[] = [
          {
            id: 'close-modal',
            type: 'button',
            className: 'e-button',
            title: f({ id: 'alarms.modal.rule.button.close' }),
            closeCallback: props.closeCallback,
          },
          {
            id: 'dynamictable-ridebook-form-submit',
            type: 'submit',
            className: 'e-button e-button--gray',
            title: f({ id: 'reports.export' }),
            submitCallback: prepareSubmit,
          },
        ];

        const datepickerCallback = () => {
          formikProps.setFieldTouched('dateFrom');
        };

        return (
          <CmdModal
            id="ridebookexport-form"
            title={f({ id: 'ridebook.report.export' })}
            {...formikProps}
            footerButtons={buttons}
            loader={loader}
          >
            <div className="filtered-list">
              <div style={{ marginBottom: '16px', padding: '0' }}>
                <CmdField
                  component={CmdCalendar}
                  key={`r05-${formData[0].indexId}`}
                  id={`r05-${formData[0].indexId}`}
                  name={formData[0].indexId}
                  label={formData[0].title}
                  placeholder=""
                  enableTime={true}
                  required={FormikValidatorService.isFieldRequired(formData[0].rules)}
                  rules={formData[0].rules}
                  // dateFormat="d.m.Y H:i"
                  onChangeCallback={datepickerCallback}
                />
              </div>
              <div style={{ marginBottom: '16px', padding: '0' }}>
                <CmdField
                  component={CmdCalendar}
                  key={`r05-${formData[1].indexId}`}
                  id={`r05-${formData[1].indexId}`}
                  name={formData[1].indexId}
                  label={formData[1].title}
                  placeholder=""
                  enableTime={true}
                  required={FormikValidatorService.isFieldRequired(formData[1].rules)}
                  rules={formData[1].rules}
                  // dateFormat="d.m.Y H:i"
                  onChangeCallback={datepickerCallback}
                />
              </div>
              <ChipSelect
                id={`${formData[2].indexId}`}
                name={`${formData[2].indexId}`}
                label={formData[2].title}
                placeholder={f({ id: 'form.choose' })}
                searchPlaceholder={f({ id: 'form.search' })}
                selectButton={f({ id: 'form.groupChipSelectUnique.button.apply' })}
                resetButton={f({ id: 'form.groupChipSelectUnique.button.reset' })}
                options={formData[2].options || []}
                required={true}
                searchTooltip={f({
                  id: 'alarms.section.vehicles.driverId.search.tooltip',
                })}
                selectButtonTooltip={f({
                  id: 'alarms.section.vehicles.driverId.selectButton.tooltip',
                })}
                resetButtonTooltip={f({
                  id: 'alarms.section.vehicles.driverId.resetButton.tooltip',
                })}
                bossCheckbox={f({ id: 'reports.chooseAllVehicles' })}
              />
              <CmdField
                component={CmdRadioButton}
                key={`r05-${formData[3].indexId}`}
                id={`r05-${formData[3].indexId}`}
                name={formData[3].indexId}
                label={formData[3].title}
                options={formData[3].options || []}
                required={FormikValidatorService.isFieldRequired(formData[3].rules)}
              />
            </div>
          </CmdModal>
        );
      }}
    </Formik>
  );
}
